/**
 * The "×" that empties a text field once something has been typed in it.
 *
 * ONE definition, because three already existed and were drifting:
 * `ui/SearchAndFilter` draws a 12px filled circle, `ui/Select/MultiSelect`
 * draws a plain glyph, `ui/SelectAllClear` draws a third — and the two search
 * boxes that had none at all (`ui/Select`'s searchable menu and
 * `ui/MultiSelect`'s) left the reader retyping or holding backspace. A field
 * whose only way back to empty is the keyboard is the odd one out on a screen
 * where its neighbours clear in one click.
 *
 * ── Plain glyph, not a filled chip ──────────────────────────────────────────
 * It sits INSIDE the field's border, over the value it removes, so it has to
 * read as a control on the text rather than a second element beside it. A
 * filled circle at 12px is the heaviest thing in a 30px-high input and pulls
 * the eye away from the value being read. `SearchAndFilter` keeps its own
 * circle: it is already shipped and visually signed off, and changing it would
 * be a redesign rather than a gap being filled.
 *
 * ── Why a `<button>` and not a bare span ────────────────────────────────────
 * It is a real action, so it needs to be reachable by keyboard and announced.
 * `tabIndex={-1}` keeps it OUT of the tab order all the same: Tab from a search
 * box should go to the next control, not to a button that only exists while the
 * box has text — and the keyboard already has a faster way to empty a field.
 * Pointer and assistive-technology users get the affordance; tab order does not
 * grow a stop that appears and disappears as you type.
 *
 * `onMouseDown`-preventDefault so the press does not blur the input: clearing
 * and then continuing to type is the common case, and a field that loses focus
 * on clear makes the reader click back into it.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';

const BUTTON_CLASS =
  'inline-flex h-4 w-4 flex-shrink-0 items-center justify-center border-0 p-0 ' +
  'bg-transparent leading-none cursor-pointer ' +
  'text-[var(--color-text-muted)] transition-[color] duration-fast ' +
  'hover:text-[var(--color-text-primary)] ' +
  'focus-visible:outline focus-visible:outline-2 ' +
  'focus-visible:outline-[var(--color-border-focus)] focus-visible:outline-offset-[1px]';

export interface InputClearButtonProps {
  /** Empty the field. The caller owns the value, so it owns the clearing. */
  onClear: () => void;
  /** Accessible name. Defaults to the shared `common.clear` string. */
  ariaLabel?: string;
  /** Appended to the button — for one-off spacing inside a particular field. */
  className?: string;
}

export function InputClearButton({
  onClear,
  ariaLabel,
  className,
}: Readonly<InputClearButtonProps>): React.ReactElement {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      tabIndex={-1}
      className={[BUTTON_CLASS, className ?? ''].filter(Boolean).join(' ')}
      aria-label={ariaLabel ?? t('common.clear', 'Clear')}
      // Keep focus in the field — see the file docblock.
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClear}
      data-testid="input-clear"
    >
      {/* A crossing glyph rather than the letter "x" — it keeps its shape at
          14px and does not inherit the font's letterform. */}
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
        <path
          d="M1 1l8 8M9 1l-8 8"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
      </svg>
    </button>
  );
}

export default InputClearButton;
